import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { Button, Input, DarkModeToggleCompact } from '../../components/ui'
import { Mail, Lock, User, Sparkles, LogIn, UserPlus, AlertCircle, CheckCircle } from 'lucide-react'

export const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const { signIn, signUp } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Check if we have a requested redirect path (like /wkpoule)
  const queryParams = new URLSearchParams(location.search)
  const redirectPath = queryParams.get('redirectTo') || '/admin/dashboard'

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setMessage('')
    setLoading(true)

    try {
      if (isLogin) {
        // LOGIN
        const { error } = await signIn(email, password)
        if (error) throw error
        navigate(redirectPath)
      } else {
        // SIGN UP
        const { data, error } = await signUp(email, password, {
          full_name: fullName
        })
        if (error) throw error

        // Check if session exists (auto-login) or if confirmation is needed
        if (data?.session) {
          navigate(redirectPath)
        } else {
          setMessage('Account aangemaakt! Controleer je e-mail voor de bevestigingslink.')
          setIsLogin(true) // Switch back to login
        }
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center p-4">
      {/* Background Gradient Blobs */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-gradient-to-br from-orange-400/20 to-pink-500/20 rounded-full blur-[120px] animate-float"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-gradient-to-br from-blue-400/20 to-purple-500/20 rounded-full blur-[120px] animate-float" style={{ animationDelay: '3s' }}></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Dark Mode Toggle */}
        <div className="absolute top-0 right-0">
          <DarkModeToggleCompact />
        </div>

        {/* Logo Header */}
        <div className="text-center mb-8 animate-slideUp">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="p-3 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-600 shadow-xl">
              <Sparkles className="text-white" size={32} />
            </div>
            <span className="font-black text-4xl tracking-tighter uppercase italic select-none">
              <span className="text-gray-900">PRO</span>
              <span className="text-orange-500 ml-[0.15rem]">POULE</span>
            </span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isLogin ? 'Welkom terug' : 'Maak een account'}
          </h1>
          <p className="text-gray-600">
            {isLogin ? 'Log in om je toernooien te beheren' : 'Start direct met organiseren'}
          </p>
        </div>

        {/* Premium Glass Card */}
        <div className="glass-strong rounded-3xl p-8 shadow-2xl animate-scaleIn">
          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <Input
                label="Volledige Naam"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jouw Naam"
                icon={<User size={18} />}
                required
              />
            )}

            <Input
              label="Emailadres"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="naam@voorbeeld.nl"
              icon={<Mail size={18} />}
              required
            />

            <div>
              <div className="flex justify-between mb-2">
                <label className="label-premium">Wachtwoord</label>
                {isLogin && (
                  <a href="#" className="text-xs text-[var(--accent-primary)] hover:underline font-medium">
                    Wachtwoord vergeten?
                  </a>
                )}
              </div>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                icon={<Lock size={18} />}
                required
                minLength={6}
              />
            </div>

            {error && (
              <div className="glass-subtle border-2 border-red-500/30 rounded-xl p-4 flex items-start gap-3 animate-slideUp">
                <AlertCircle className="text-red-500 flex-shrink-0" size={20} />
                <p className="text-sm text-red-600 font-medium">{error}</p>
              </div>
            )}

            {message && (
              <div className="glass-subtle border-2 border-green-500/30 rounded-xl p-4 flex items-start gap-3 animate-slideUp">
                <CheckCircle className="text-green-500 flex-shrink-0" size={20} />
                <p className="text-sm text-green-600 font-medium">{message}</p>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={loading}
              icon={isLogin ? <LogIn size={20} /> : <UserPlus size={20} />}
            >
              {loading ? 'Even geduld...' : (isLogin ? 'Inloggen' : 'Account Aanmaken')}
            </Button>
          </form>

          <div className="mt-8 text-center border-t border-white/20 pt-6">
            <p className="text-gray-600 text-sm">
              {isLogin ? 'Nog geen account?' : 'Al een account?'}{' '}
              <span
                className="text-[var(--accent-primary)] font-bold cursor-pointer hover:underline"
                onClick={() => { setIsLogin(!isLogin); setError(''); setMessage(''); }}
              >
                {isLogin ? 'Maak er één aan' : 'Log hier in'}
              </span>
            </p>
            <p className="text-gray-400 text-xs mt-4">
              © 2026 Clarity Solutions
            </p>
          </div>
        </div>

        {/* Features */}
        <div className="mt-8 grid grid-cols-3 gap-4 text-center">
          <div className="glass-subtle rounded-xl p-4">
            <div className="text-2xl mb-2">⚡</div>
            <p className="text-xs text-gray-600 font-medium">Snel & Eenvoudig</p>
          </div>
          <div className="glass-subtle rounded-xl p-4">
            <div className="text-2xl mb-2">🔒</div>
            <p className="text-xs text-gray-600 font-medium">100% Veilig</p>
          </div>
          <div className="glass-subtle rounded-xl p-4">
            <div className="text-2xl mb-2">🚀</div>
            <p className="text-xs text-gray-600 font-medium">Direct Starten</p>
          </div>
        </div>
      </div>
    </div>
  )
}
